import { searchErrorLogger } from '@/core/Logger';

interface NativeRefs {
  execRegExp: (regex: RegExp, target: string) => RegExpExecArray | null;
  stringReplace: (target: string, searchValue: RegExp | string, replaceValue: string | ((...args: any[]) => string)) => string;
  stringSplit: (target: string, separator: RegExp | string) => string[];
  stringTrim: (value: string) => string;
  objectKeys: (value: unknown) => string[];
  objectEntries: (value: unknown) => Array<[string, unknown]>;
}

type MethodGetter = () => unknown;

type NativeSource = {
  RegExp: RegExpConstructor;
  String: StringConstructor;
  Object: ObjectConstructor;
};

const METHOD_CHECKS: Array<{ name: string; getter: MethodGetter }> = [
  { name: 'RegExp.prototype.exec', getter: () => RegExp.prototype.exec },
  { name: 'String.prototype.replace', getter: () => String.prototype.replace },
  { name: 'String.prototype.split', getter: () => String.prototype.split },
  { name: 'String.prototype.trim', getter: () => String.prototype.trim },
  { name: 'Object.keys', getter: () => Object.keys },
  { name: 'Object.entries', getter: () => Object.entries }
];

let cachedRefs: NativeRefs | null = null;
let cachedForceState = false;

function isLikelyNative(candidate: unknown): boolean {
  if (typeof candidate !== 'function') {
    return false;
  }

  const source = Function.prototype.toString.call(candidate);
  return source.includes('[native code]');
}

function createIsolatedWindow(): Window | null {
  if (typeof document === 'undefined') {
    return null;
  }

  try {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.tabIndex = -1;
    iframe.src = 'about:blank';

    document.documentElement?.appendChild(iframe);
    return iframe.contentWindow ?? null;
  } catch (error) {
    searchErrorLogger.log('[SmartSearch] Unable to create isolated iframe for native methods.', error);
    return null;
  }
}

function logContaminationWarning(): void {
  const warningStyle = 'color: #fff; background: #d32f2f; font-size: 16px; font-weight: 700; padding: 4px 8px; border-radius: 4px;';
  searchErrorLogger.log('%c[SmartSearch] Detected overridden native JavaScript methods in the client environment!', warningStyle);
  searchErrorLogger.log('[SmartSearch] Enable the "repair native methods" option in SmartSearch configuration or remove external scripts that patch built-in prototypes.');
}

function shouldForceNativeMethods(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return Boolean(window._QONizer?.customSmartSearchConfig?.forceNativeMethods);
  } catch {
    return false;
  }
}

export function getNativeRefs(): NativeRefs {
  const forceRepair = shouldForceNativeMethods();

  if (cachedRefs) {
    if (!forceRepair || cachedForceState === forceRepair) {
      return cachedRefs;
    }
    cachedRefs = null;
  }

  const isContaminated = METHOD_CHECKS.some(check => !isLikelyNative(check.getter()));

  let sourceGlobal: NativeSource = typeof window !== 'undefined'
    ? (window as unknown as NativeSource)
    : (globalThis as NativeSource);

  if (isContaminated) {
    logContaminationWarning();
    if (!forceRepair) {
      searchErrorLogger.log('[SmartSearch] Skipping native method repair because customSmartSearchConfig.forceNativeMethods is disabled.');
    }
  }

  if (forceRepair) {
    const isolated = createIsolatedWindow();
    if (isolated) {
      if (isContaminated) {
        console.info('[SmartSearch] Restored native methods from isolated iframe due to detected prototype overrides.');
      } else {
        console.info('[SmartSearch] Restored native methods from isolated iframe as requested by forceNativeMethods flag.');
      }
      sourceGlobal = isolated as unknown as NativeSource;
    } else if (isContaminated) {
      searchErrorLogger.log('[SmartSearch] Failed to restore native methods. Falling back to patched environment.');
    }
  }

  const regExpProto = sourceGlobal.RegExp.prototype;
  const stringProto = sourceGlobal.String.prototype;
  const objectCtor = sourceGlobal.Object;
  const replaceFn = stringProto.replace as unknown as (this: string, searchValue: RegExp | string, replaceValue: string | ((...args: any[]) => string)) => string;
  const splitFn = stringProto.split as unknown as (this: string, separator: RegExp | string) => string[];
  const trimFn = stringProto.trim as unknown as (this: string) => string;
  const normalizeObjectTarget = (value: unknown): object => {
    if (value != null && (typeof value === 'object' || typeof value === 'function')) {
      return value as object;
    }
    return {};
  };

  cachedRefs = {
    execRegExp: (regex: RegExp, target: string) => regExpProto.exec.call(regex, target),
    stringReplace: (target: string, searchValue: RegExp | string, replaceValue: string | ((...args: any[]) => string)) =>
      replaceFn.call(target, searchValue, replaceValue),
    stringSplit: (target: string, separator: RegExp | string) =>
      splitFn.call(target, separator),
    stringTrim: (value: string) => trimFn.call(value),
    objectKeys: (value: unknown) => objectCtor.keys(normalizeObjectTarget(value)) as string[],
    objectEntries: (value: unknown) => objectCtor.entries(normalizeObjectTarget(value)) as Array<[string, unknown]>
  };

  cachedForceState = forceRepair;

  return cachedRefs;
}
